// CorreccionRegistroDialog — capacidad "Corrección de datos con motivo".
//
// El coordinador (capacidad `puede_corregir_datos`) usa este modal desde el
// drill-down hora-a-hora (LineaHoraAHoraDialog) en DOS modos:
//
//   - "corregir": precarga los valores de un registro existente y dispara un
//     PATCH parcial (solo manda lo que cambió) + motivo ≥20 obligatorio.
//   - "alta": celda vacía (hora sin captura). Precarga fecha/turno/hora/línea
//     (read-only, identidad temporal inmutable) y dispara un POST de alta
//     retroactiva + motivo ≥20 obligatorio.
//
// El SERVER re-valida la autorización y la capacidad; traducimos los códigos
// de error tipados (out_of_scope, duplicate, invalid_state, check_violation,
// capacidad_no_autorizada) a toasts claros vía onError.
//
// Sigue el patrón de EditarHoraDialog (estado controlado + validación manual +
// `.parse()` en el hook de mutación), NO RHF: es el patrón vigente de los
// dialogs hermanos del repo (líder/supervisor) y mantiene la consistencia.

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { AltaRetroactivaRegistro, CorreccionRegistro, RegistroProduccionHora } from 'schemas';
import { Button } from '../../components/ui/button';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import {
  useCorregirRegistro,
  useCrearRegistroRetroactivo,
  useSkusByLinea,
} from '../../lib/api/queries';
import { CantidadInput } from '../captura/shared';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { limpiarEntradaLote, propsInputLote, validarLoteUi } from '../../lib/formatoLote';

/** Contexto para una celda vacía (modo "alta") — identidad temporal del bloque. */
export interface CeldaVaciaContexto {
  fecha: string;
  turno_id: string;
  linea_id: string;
  hora_inicio_bloque: string; // "HH:MM" o "HH:MM:SS"
  hora_fin_bloque: string; // "HH:MM" o "HH:MM:SS"
  /** La celda ya tiene cobertura y solo se está completando un hueco. */
  es_hueco_parcial?: boolean;
  /** Líder programado para esa línea/turno/fecha (para lider_asignado_user_id). */
  lider_asignado_user_id?: string | null;
}

type Modo =
  | { tipo: 'corregir'; registro: RegistroProduccionHora }
  | { tipo: 'alta'; celda: CeldaVaciaContexto };

interface Props {
  /** null → modal cerrado. */
  modo: Modo | null;
  onClose: () => void;
  /** Callback opcional al guardar OK (ej. cerrar también un menú padre). */
  onSaved?: () => void;
}

const MIN_RAZON = 20;

/** Mapea los códigos de error del backend a un mensaje claro en español. */
function tituloErrorCorreccion(err: ApiError): string {
  switch (err.code) {
    case 'out_of_scope':
      return 'La línea no pertenece a su planta';
    case 'duplicate':
      return 'Ya existe un registro para ese bloque';
    case 'invalid_state':
      return 'El registro no está en un estado corregible';
    case 'check_violation':
      return 'Los datos no cumplen una validación de negocio';
    case 'capacidad_no_autorizada':
      return 'No tiene la capacidad de corregir datos';
    default:
      return 'No se pudo guardar la corrección';
  }
}

export function CorreccionRegistroDialog({ modo, onClose, onSaved }: Props) {
  const open = modo !== null;
  const corregirMut = useCorregirRegistro();
  const altaMut = useCrearRegistroRetroactivo();
  const pending = corregirMut.isPending || altaMut.isPending;

  // La línea de la que sacamos los SKUs disponibles depende del modo.
  const lineaId =
    modo?.tipo === 'corregir' ? modo.registro.linea_id : (modo?.celda.linea_id ?? null);
  const skusQ = useSkusByLinea(lineaId);
  const skus = useMemo(() => skusQ.data ?? [], [skusQ.data]);

  const [skuId, setSkuId] = useState('');
  const [cantidad, setCantidad] = useState<number | null>(null);
  const [operarios, setOperarios] = useState<number | null>(null);
  const [golpes, setGolpes] = useState<number | null>(null);
  const [op, setOp] = useState('');
  const [razon, setRazon] = useState('');
  const { formatoLote } = useTenantConfig();
  const inputLote = propsInputLote(formatoLote);

  // Pre-fill al abrir / cambiar de modo.
  useEffect(() => {
    if (!modo) return;
    if (modo.tipo === 'corregir') {
      const r = modo.registro;
      setSkuId(r.sku_id);
      setCantidad(r.cantidad_procesada);
      setOperarios(r.numero_operarios);
      setGolpes(r.golpes_por_minuto);
      setOp(r.op_lote_oc_cliente ?? '');
    } else {
      // Alta: celda vacía → todo en blanco salvo defaults razonables.
      setSkuId('');
      setCantidad(null);
      setOperarios(null);
      setGolpes(null);
      setOp('');
    }
    setRazon('');
  }, [modo]);

  const selectedSku = skus.find((s) => s.id === skuId) ?? null;
  const golpesNoAplica = selectedSku ? !selectedSku.golpes_min_aplica : false;

  // Si el SKU activo no usa golpes/min, forzar null (evita valor fantasma).
  useEffect(() => {
    if (selectedSku && !selectedSku.golpes_min_aplica) setGolpes(null);
  }, [selectedSku]);

  if (!modo) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogHeader>
          <DialogTitle>Corrección de datos</DialogTitle>
        </DialogHeader>
        <DialogContent />
      </Dialog>
    );
  }

  const esAlta = modo.tipo === 'alta';
  const horaIni =
    modo.tipo === 'corregir'
      ? String(modo.registro.hora_inicio_bloque).slice(0, 5)
      : modo.celda.hora_inicio_bloque.slice(0, 5);
  const horaFin =
    modo.tipo === 'corregir'
      ? String(modo.registro.hora_fin_bloque).slice(0, 5)
      : modo.celda.hora_fin_bloque.slice(0, 5);
  const fecha = modo.tipo === 'corregir' ? modo.registro.fecha : modo.celda.fecha;

  function validar(): string | null {
    if (!skuId) return 'Elija un SKU';
    if (cantidad === null || cantidad < 0) return 'Cantidad procesada inválida';
    // En alta no permitimos 0 (el 0 exige paros que cubran el bloque, y este
    // modal no captura paros). El backend re-valida igual.
    if (esAlta && cantidad === 0) return 'En un alta retroactiva la cantidad debe ser mayor a 0';
    if (operarios === null || operarios < 1 || operarios > 20)
      return 'Operarios debe estar entre 1 y 20';
    if (cantidad !== 0 && selectedSku?.golpes_min_aplica && (golpes === null || golpes <= 0))
      return 'Golpes/min es obligatorio para este SKU';
    // Formato de la empresa (B7). En corrección solo si el lote cambió: uno
    // guardado con un formato anterior no bloquea corregir otros campos.
    const loteCambio =
      esAlta ||
      op.trim() !== ((modo?.tipo === 'corregir' && modo.registro.op_lote_oc_cliente) || '');
    if (op.trim().length === 0) return 'OP / Lote es obligatorio';
    if (loteCambio) {
      const loteOk = validarLoteUi(op, formatoLote);
      if (loteOk !== true) return loteOk;
    }
    if (razon.trim().length < MIN_RAZON)
      return `El motivo debe tener al menos ${MIN_RAZON} caracteres`;
    return null;
  }

  function manejarError(err: unknown): void {
    if (err instanceof ApiError) {
      toast.error(tituloErrorCorreccion(err), { description: err.message });
    } else {
      toast.error('No se pudo guardar la corrección', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    }
  }

  async function handleGuardar() {
    if (!modo) return;
    const errMsg = validar();
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    const razonTrim = razon.trim();

    try {
      if (modo.tipo === 'corregir') {
        const r = modo.registro;
        // PATCH parcial: solo los campos que cambiaron + razon obligatorio.
        const cambios: CorreccionRegistro = { razon: razonTrim };
        if (skuId !== r.sku_id) cambios.sku_id = skuId;
        if (cantidad !== r.cantidad_procesada) cambios.cantidad_procesada = cantidad!;
        if (operarios !== r.numero_operarios) cambios.numero_operarios = operarios!;
        if (golpes !== r.golpes_por_minuto) cambios.golpes_por_minuto = golpes;
        if (op.trim() !== (r.op_lote_oc_cliente ?? '')) cambios.op_lote_oc_cliente = op.trim();

        const hayCambios = Object.keys(cambios).some((k) => k !== 'razon');
        if (!hayCambios) {
          toast.error('No modificaste ningún campo');
          return;
        }
        await corregirMut.mutateAsync({ registroId: r.id, cambios });
        toast.success('Registro corregido');
      } else {
        const c = modo.celda;
        const payload: AltaRetroactivaRegistro = {
          fecha: c.fecha,
          turno_id: c.turno_id,
          hora_inicio_bloque: c.hora_inicio_bloque,
          hora_fin_bloque: c.hora_fin_bloque,
          linea_id: c.linea_id,
          sku_id: skuId,
          cantidad_procesada: cantidad!,
          cantidad_kg: null,
          golpes_por_minuto: golpes,
          numero_operarios: operarios!,
          op_lote_oc_cliente: op.trim(),
          es_captura_fuera_turno: false,
          razon_fuera_turno: null,
          capturado_por_supervisor: false,
          razon_delegacion: null,
          lider_asignado_user_id: c.lider_asignado_user_id ?? null,
          origen_solicitud: 'programa',
          programado: true,
          paros: [],
          razon: razonTrim,
        };
        await altaMut.mutateAsync(payload);
        toast.success('Registro creado por corrección');
      }
      onClose();
      onSaved?.();
    } catch (err) {
      manejarError(err);
    }
  }

  const skuOpts: ComboboxOption[] = skus.map((s) => ({
    value: s.id,
    label: s.codigo_cliente,
    description: `${s.descripcion}${s.formato ? ` · ${s.formato}` : ''}`,
    searchText: `${s.codigo_cliente} ${s.descripcion} ${s.formato ?? ''}`,
  }));

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>
          {esAlta
            ? modo.celda.es_hueco_parcial
              ? 'Completar tramo faltante'
              : 'Agregar registro faltante'
            : 'Corregir bloque'}{' '}
          {horaIni}–{horaFin}
        </DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="rounded bg-surface-overlay p-2 text-xs text-fg-secondary">
          Fecha <strong>{fecha}</strong> · bloque {horaIni}–{horaFin}
          <br />
          {esAlta
            ? 'Alta retroactiva: la fecha, el turno, la hora y la línea quedan fijos para este bloque.'
            : 'La fecha, el turno y la hora del bloque no se pueden cambiar.'}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="corr-sku">SKU</Label>
          {skusQ.isLoading ? (
            <p className="text-xs text-fg-tertiary">Cargando SKUs…</p>
          ) : (
            <Combobox
              id="corr-sku"
              options={skuOpts}
              value={skuId}
              onChange={setSkuId}
              placeholder="Buscar por código o descripción…"
              emptyLabel="Sin coincidencias"
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CantidadInput
            id="corr-cantidad"
            label="Cantidad procesada"
            placeholder="Ej. 1.250"
            value={cantidad}
            onChange={setCantidad}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="corr-operarios">Operarios (1–20)</Label>
            <Input
              id="corr-operarios"
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              step={1}
              placeholder="Ej. 3"
              value={operarios ?? ''}
              onChange={(e) => setOperarios(e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="corr-golpes">
            Golpes/min{golpesNoAplica ? ' (no aplica a este SKU)' : ''}
          </Label>
          <Input
            id="corr-golpes"
            type="number"
            inputMode="decimal"
            min={1}
            step={0.1}
            disabled={golpesNoAplica}
            placeholder={golpesNoAplica ? 'No aplica' : 'Ej. 80'}
            value={golpes ?? ''}
            onChange={(e) => setGolpes(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="corr-op">OP / Lote / OC Cliente</Label>
          <Input
            id="corr-op"
            type="text"
            inputMode={inputLote.inputMode}
            maxLength={inputLote.maxLength}
            placeholder={inputLote.placeholder}
            value={op}
            onChange={(e) => setOp(limpiarEntradaLote(e.target.value, formatoLote))}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="corr-razon">
            Motivo de la {esAlta ? 'alta' : 'corrección'} (mínimo {MIN_RAZON} caracteres)
          </Label>
          <Textarea
            id="corr-razon"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            placeholder={
              esAlta
                ? 'Ej: el líder no capturó esta hora en planta; se reconstruye desde la planilla física firmada'
                : 'Ej: corrección de cantidad por error de digitación detectado en la planilla física del turno'
            }
          />
          <p
            className={`text-xs ${
              razon.trim().length >= MIN_RAZON ? 'text-ok' : 'text-fg-tertiary'
            }`}
          >
            {razon.trim().length}/{MIN_RAZON} caracteres mínimos
          </p>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancelar
        </Button>
        <Button onClick={handleGuardar} disabled={pending || razon.trim().length < MIN_RAZON}>
          {pending ? (
            <>
              <Spinner className="border-white/30 border-t-white" />{' '}
              {esAlta ? 'Creando…' : 'Guardando…'}
            </>
          ) : esAlta ? (
            'Crear registro'
          ) : (
            'Guardar corrección'
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
